import React, { useState } from 'react';
import { motion } from 'framer-motion';
import './ContactForm.css';

const ContactForm = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    company: '',
    message: '',
    website: '' // Honeypot field
  });

  const [status, setStatus] = useState('');
  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ''
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Name validation
    if (formData.fullName.trim().length < 2) {
      newErrors.fullName = 'Name must be at least 2 characters';
    }
    
    // Email validation (basic)
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    // Message validation
    if (formData.message.trim().length < 10) {
      newErrors.message = 'Message must be at least 10 characters';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    if (!validateForm()) {
      return;
    }
    
    // Check honeypot - if filled, it's likely a bot
    if (formData.website) {
      setStatus('success'); // Fake success for bots
      return;
    }
    
    setStatus('sending');

    try {
      // Web3Forms submission
      const accessKey = process.env.REACT_APP_WEB3FORMS_ACCESS_KEY;
      if (!accessKey) {
        console.error('Missing REACT_APP_WEB3FORMS_ACCESS_KEY');
        setStatus('error');
        return;
      }

      const formPayload = new FormData();
      formPayload.append('access_key', accessKey);
      formPayload.append('name', formData.fullName);
      formPayload.append('email', formData.email);
      formPayload.append('company', formData.company);
      formPayload.append('message', formData.message);
      formPayload.append('subject', `New Contact Form Submission from ${formData.fullName}`);
      
      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        body: formPayload
      });

      const data = await response.json();

      if (data.success) {
        setStatus('success');
        setFormData({ fullName: '', email: '', company: '', message: '', website: '' });
        setErrors({});
      } else {
        setStatus('error');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setStatus('error');
    }
  };

  return (
    <section className="contact-form-section">
      <div className="contact-container">
        <motion.div
          className="contact-header"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <div className="section-badge">
            <span className="badge-icon">💬</span>
            LET'S CONNECT
          </div>
          <h2 className="contact-title">Let's Talk About Your Project</h2>
          <p className="contact-description">
            Whether you're looking to modernise your website, introduce online bookings, or 
            streamline how your business operates, we'll help you explore the right solution <span className="gradient-text">without pressure or jargon</span>.
          </p>
          <p className="contact-subdescription">
            Tell us a little about your business and what you're hoping to achieve, and we'll 
            take it from there.
          </p>
        </motion.div>

        <div className="contact-grid">
          <motion.form
            className="contact-form"
            onSubmit={handleSubmit}
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            aria-label="Contact form to start a project"
          >
            <div className="form-group">
              <label htmlFor="fullName">Full Name *</label>
              <input
                type="text"
                id="fullName"
                name="fullName"
                placeholder="John Doe"
                value={formData.fullName}
                onChange={handleChange}
                maxLength={100}
                required
              />
              {errors.fullName && <span className="error-message">{errors.fullName}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="email">Email Address *</label>
              <input
                type="email"
                id="email"
                name="email"
                placeholder="john@example.com"
                value={formData.email}
                onChange={handleChange}
                maxLength={255}
                required
              />
              {errors.email && <span className="error-message">{errors.email}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="company">Company Name</label>
              <input
                type="text"
                id="company"
                name="company"
                placeholder="Your Company"
                value={formData.company}
                onChange={handleChange}
                maxLength={100}
              />
            </div>

            {/* Honeypot field - hidden from real users */}
            <div className="form-group" style={{ position: 'absolute', left: '-9999px', opacity: 0, pointerEvents: 'none' }} aria-hidden="true">
              <label htmlFor="website">Website</label>
              <input
                type="text"
                id="website"
                name="website"
                tabIndex="-1"
                autoComplete="off"
                value={formData.website}
                onChange={handleChange}
              />
            </div>

            <div className="form-group">
              <label htmlFor="message">Message *</label>
              <textarea
                id="message"
                name="message"
                placeholder="Tell us about your project..."
                rows="6"
                value={formData.message}
                onChange={handleChange}
                maxLength={2000}
                required
              />
              {errors.message && <span className="error-message">{errors.message}</span>}
              <span className="char-count">{formData.message.length}/2000</span>
            </div>

            <button type="submit" className="btn-submit" disabled={status === 'sending'} aria-label="Send contact form message to Leviathan Coding Systems">
              <span>{status === 'sending' ? 'Sending...' : 'Send Message'}</span>
              <svg className="send-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <path d="M22 2L11 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M22 2L15 22L11 13L2 9L22 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>

            {status === 'success' && (
              <p className="form-message success">Message sent successfully! We'll be in touch soon.</p>
            )}
            {status === 'error' && (
              <p className="form-message error">Something went wrong. Please try again.</p>
            )}
            {status === 'rate-limit' && (
              <p className="form-message error">Too many submissions. Please wait a few minutes and try again.</p>
            )}
          </motion.form>

          <motion.div
            className="contact-info"
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <div className="info-card">
              <div className="info-card-header">
                <span className="info-icon">&gt;_</span>
                <h3>Based in the North West of England</h3>
              </div>
              <p>
                Leviathan Coding Systems works with businesses across the UK, delivering 
                reliable, thoughtfully engineered digital solutions.
              </p>
            </div>

            <div className="info-item">
              <div className="info-item-icon">📧</div>
              <div className="info-item-content">
                <h4>Email</h4>
                <p>contact@leviathancodingsystems.com</p>
                <span className="info-note">We typically respond within 24 hours</span>
              </div>
            </div>

            <div className="info-item">
              <div className="info-item-icon">📍</div>
              <div className="info-item-content">
                <h4>Location</h4>
                <p>North West England</p>
                <span className="info-note">Serving businesses across the UK and beyond</span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ContactForm;
